import { StatusCodes } from 'http-status-codes';
import { ProductAPIQueryBuilder } from '@api/builders/product';
import getAPIProduct from '@api/product';
import getAPIService from '@api/service';
import { HttpRejection } from '@model/http/rejection';
import type { AugmentedStore } from '@model/vue-store/augmented-store';
import { Logger } from '@utils/logger';
import { papiFetchAllPagesClient } from '@utils/papi-fetch-paginated-client';
import type { RawAxiosRequestConfig, AxiosResponse } from 'axios';
import type { ActionContext, Store } from 'vuex';

export default {
    namespaced: true,

    state: () => ({
        pricingMethod: 1,
        currency: null,
        discountAfter: null,
        group: null,
        requirements: {
            workloads: 20,
            storage: 200,
        },
        unitPrices: [],
        currentPriceList: {
            id: null,
            name: null,
        },
        priceLists: [],
        prices: null,
        items: {},
    }),

    actions: {
        async createLeadRequest(store: never, data: Record<string, any>) {
            const client = getAPIService(this.$context);

            // eslint-disable-next-line @typescript-eslint/naming-convention
            const { _recaptchaResponse, ...payload } = data;

            const cfg: RawAxiosRequestConfig = {
                method: 'POST',
                url: '/svc/v1/marketing/forms/leads',
                timeout: 60 * 1000,
                validateStatus: (status: number) => status === 200,
                ...(_recaptchaResponse && { headers: { 'X-GRecaptcha-Response': _recaptchaResponse } }),
                data: payload,
            };

            const response = await client.request(cfg);
            return response;
        },

        async updateLeadRequest(store: never, data: Record<string, any>) {
            const client = getAPIService(this.$context);

            // eslint-disable-next-line @typescript-eslint/naming-convention
            const { _recaptchaResponse, ...payload } = data;

            const cfg: RawAxiosRequestConfig = {
                method: 'PUT',
                url: `/svc/v1/marketing/forms/leads/${payload.osauid}`,
                timeout: 60 * 1000,
                validateStatus: (status: number) => status === 200,
                ...(_recaptchaResponse && { headers: { 'X-GRecaptcha-Response': _recaptchaResponse } }),
                data: payload,
            };

            const response = await client.request(cfg);
            return response;
        },

        async getSyncedData(this: AugmentedStore, store, args) {
            const { key, locale } = args;
            const data = await this.$context.Synced.vroiData.query(key, locale);
            store.commit('setSyncedData', { key, data });
        },

        async getEntity(store: ActionContext<any, any>, args: { request: Storage, httpRejectEmptyState: boolean }) {
            const request = args.request;
            const client = getAPIProduct(this.$context);

            const opts: RawAxiosRequestConfig = {
                validateStatus: (status: StatusCodes) => status === StatusCodes.OK,
                timeout: typeof window !== 'undefined' ? 30000 : 6000,
                method: 'GET',
                url: request.entityPath,
                params: request.params,
            };

            try {
                const response = await client.request(opts);
                store.commit('setEntity', { request, response });
            } catch (error) {
                Logger.error({ error });
                const httpStatus = error.status || StatusCodes.BAD_GATEWAY;
                const message = error.message || 'Premature rendering stop';
                throw new HttpRejection(message, httpStatus);
            }
        },

        async getPriceLists(store: ActionContext<any, any>) {
            const queryBuilder: ProductAPIQueryBuilder = new ProductAPIQueryBuilder('priceLists')
                .setEntityPath('/api/finance/price-lists/')
                .setPaginate(1, 50);
            const request = queryBuilder.toObject();

            await store.dispatch('getEntity', { request });
        },

        async getPricesForPriceBook(
            _store: ActionContext<any, any>,
            args: { productCodes: string[], priceListId: number },
        ) {
            if (typeof window === 'undefined') return [];

            const { productCodes, priceListId } = args;
            if (!priceListId) {
                throw new Error('Cannot fetch prices without a price list ID');
            }

            const perPage = 50;
            const client = getAPIProduct(this.$context);

            const responses = await papiFetchAllPagesClient(client, (page) => {
                const request = new ProductAPIQueryBuilder('unitPrices')
                    .setEntityPath('/api/finance/price-lists/prices')
                    .addMatchesAll('price_list_id', '=', priceListId.toString())
                    .addMatchesAll('product_code', 'in', productCodes)
                    .setPaginate(page, perPage)
                    .toObject();

                return {
                    method: 'GET',
                    url: request.entityPath,
                    params: request.params,
                };
            });

            return responses.flatMap((r) => r.data.data);
        },
    },
    mutations: {
        setEntity(state: Store<any>, args: { request: Storage, response: AxiosResponse }) {
            state[args.request.id] = args.response.data.data;
        },
        setSyncedData(state, { key, data }) {
            state.items[key] = data;
        },
        setDiscountAfter(state, payload) {
            state.discountAfter = payload;
        },
        setCurrency(state, payload) {
            state.currency = payload;
        },
        setPricingMethod(state, payload) {
            state.pricingMethod = payload;
        },
        setCurrentPriceList(state, payload) {
            state.currentPriceList.id = payload.id;
            state.currentPriceList.name = payload.name;
        },
        setUnitPrices(state, payload) {
            state.unitPrices = payload;
        },
        setGroup(state, payload) {
            state.group = payload;
        },
        setStorageOptions(state, payload) {
            state.storageOptions = payload;
        },
    },
};
